'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { calculateOneWayANOVA } from '../../../lib/stats';
import { AnovaCalculator, type AnovaResult } from './anova-calculator';

// Type for ANOVA specific control values
export interface AnovaControlValues extends ControlValues {
  [ControlIds.Values]: string[];
  [ControlIds.Alpha]: number;
}

// Define the controls for ANOVA
export const anovaControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Values,
          type: ControlType.MultiColumnSelect,
          label: 'Groups (select columns)',
          required: true,
          numericOnly: true,
          minSelections: 2,
          maxSelections: 10
        } satisfies MultiColumnSelectControl,
        {
          id: ControlIds.Alpha,
          type: ControlType.Number,
          label: 'Significance Level (α)',
          required: true,
          defaultValue: 0.05,
          min: 0.001,
          max: 0.1,
          step: 0.001
        } satisfies NumberControl
      ]
    }
  ]
};

// Generate normally distributed data
function generateNormalData(mean: number, stdDev: number, size: number): string[] {
  return Array.from({ length: size }, () => {
    let u = 0, v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    return (z * stdDev + mean).toFixed(2);
  });
}

const initialData = {
  columns: [
    { 
      id: 'control', 
      name: 'Control Group', 
      values: generateNormalData(50, 4, 30) // mean: 50, std dev: 4, 30 samples
    },
    {
      id: 'treatment_a',
      name: 'Treatment A',
      values: generateNormalData(52, 4, 30) // mean: 52, std dev: 4, 30 samples
    },
    {
      id: 'treatment_b',
      name: 'Treatment B',
      values: generateNormalData(54, 4, 30) // mean: 54, std dev: 4, 30 samples
    }
  ]
};

export const anovaConfig: CalculatorConfig<CalculatorId.ANOVA> = {
  id: CalculatorId.ANOVA,
  type: CalculatorType.Graph,
  name: 'ANOVA Calculator',
  initialData: TableDataJSON.from(initialData),
  initialControlValues: {
    [ControlIds.Values]: ['control', 'treatment_a', 'treatment_b'],
    [ControlIds.Alpha]: 0.05
  },
  controls: anovaControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Values]: numericColumns.slice(0, 3).map(col => col.id),
      [ControlIds.Alpha]: 0.05
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const anovaControls = controls as AnovaControlValues;
    
    if (!data?.columns?.length || !anovaControls[ControlIds.Values]?.length) {
      return null;
    }

    const selectedColumns = anovaControls[ControlIds.Values]
      .map(id => data.columns.find(col => col.id === id))
      .filter((col): col is typeof data.columns[0] => col !== undefined);

    if (selectedColumns.length < 2) {
      return null;
    }

    // Convert string values to numbers and prepare groups
    const groups = selectedColumns.map(column => 
      column.values
        .map(val => Number(val))
        .filter(val => !isNaN(val))
    );

    // Calculate ANOVA
    const anovaResult = calculateOneWayANOVA({ groups });
    
    // Create full result object with significance check
    const result: AnovaResult = {
      ...anovaResult,
      groups,
      significantDifference: anovaResult.pValue < (anovaControls[ControlIds.Alpha] ?? 0.05)
    };

    return result;
  },
  render: ({ visualizationData }) => <AnovaCalculator visualizationData={visualizationData} />
}; 